/**
 * Session cache for customer/company dropdown refs (ticket profile + dynamic tabs).
 * TTL keeps admin master-data edits from sticking forever in one tab session.
 */
import CustomerGroupProvider from '@/resources/CustomerGroupProvider'
import CustomerTypeProvider from '@/resources/CustomerTypeProvider'
import CompanyGroupProvider from '@/resources/CompanyGroupProvider'
import CompanyDepartmentProvider from '@/resources/CompanyDepartmentProvider'
import CompanyCenterProvider from '@/resources/CompanyCenterProvider'

const CustomerGroupService = new CustomerGroupProvider()
const CustomerTypeService = new CustomerTypeProvider()
const CompanyGroupService = new CompanyGroupProvider()
const CompanyDepartmentService = new CompanyDepartmentProvider()
const CompanyCenterService = new CompanyCenterProvider()

const skipLoading = { skipLoading: true }
const CUSTOMER_REF_TTL_MS = 5 * 60 * 1000

let cache = null
let cacheAt = 0
let inflight = null

export function invalidateCustomerRefCache () {
  cache = null
  cacheAt = 0
  inflight = null
}

function isCacheFresh () {
  return cache && (Date.now() - cacheAt) < CUSTOMER_REF_TTL_MS
}

async function fetchCustomerRefsBundle () {
  const [
    customerGroup,
    customerType,
    companyGroup,
    companyDepartment,
    companyCenter
  ] = await Promise.all([
    CustomerGroupService.findAllGroup(skipLoading),
    CustomerTypeService.findAllType(skipLoading),
    CompanyGroupService.findAllGroup(skipLoading),
    CompanyDepartmentService.findAllCompanyDepartment(skipLoading),
    CompanyCenterService.findAllCompanyCenter(skipLoading)
  ])

  return {
    customerGroup: customerGroup?.data,
    customerType: customerType?.data,
    companyGroup: companyGroup?.data,
    companyDepartment: companyDepartment?.data,
    companyCenter: companyCenter?.data
  }
}

export async function loadCustomerRefs () {
  if (isCacheFresh()) return cache
  if (inflight) return inflight

  inflight = (async () => {
    try {
      cache = await fetchCustomerRefsBundle()
      cacheAt = Date.now()
      return cache
    } finally {
      inflight = null
    }
  })()
  return inflight
}

export function prefetchCustomerRefs () {
  return loadCustomerRefs().catch((error) => {
    console.warn('prefetchCustomerRefs failed:', error?.message || error)
  })
}

export default loadCustomerRefs
